import { contestRuleSchema, contestTypeSchema, lifecycleStatusSchema, listByContestSortSchema, prizeDistributionItemSchema, rewardStatusSchema, runtimeStatusSchema, submitArtworkSourceSchema, voteTypeSchema } from "./schemas.mjs";
import { localizedStringSchema } from "../shared/localizedString.mjs";
import { artworkListItemSchema } from "../artworkItem.mjs";
import { urlSlugSchema } from "../shared/urlSlug.mjs";
import { offsetPaginationInputSchema } from "../util/offsetPagination.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/contestV2/index.ts
/**
* Mirror of `SLUG_RESERVED_WORDS` from
* `src/service/contest/constants.ts`. Kept inline so the contract
* package stays free of server-only imports; the canonical definition
* stays on the server alongside the contest service code.
*/
const SLUG_RESERVED_WORDS = [
	"list",
	"banner",
	"create",
	"created",
	"follow",
	"artwork"
];
const createOffsetListResponseSchema = (itemSchema) => z.object({
	data: z.array(itemSchema),
	page: z.number().int().min(1),
	pageSize: z.number().int().min(1),
	totalPage: z.number().int().nonnegative(),
	totalCount: z.number().int().nonnegative().optional()
});
const slugParamSchema = urlSlugSchema.refine((value) => !SLUG_RESERVED_WORDS.includes(value), { message: "slug is a reserved word" });
const proposedTackNameSchema = z.string().min(1).max(100);
const contestV2Schema = z.object({
	id: z.string(),
	userId: z.string(),
	title: localizedStringSchema,
	slug: z.string().nullable(),
	mediaId: z.string(),
	tackId: z.string().nullable(),
	proposedTackName: proposedTackNameSchema.nullable(),
	type: contestTypeSchema,
	lifecycleStatus: lifecycleStatusSchema,
	runtimeStatus: runtimeStatusSchema.nullable(),
	voteType: voteTypeSchema,
	description: localizedStringSchema,
	rules: z.array(contestRuleSchema),
	prizeAmount: z.number().int(),
	prizeDistribution: z.array(prizeDistributionItemSchema),
	rewardStatus: rewardStatusSchema.nullable(),
	rewardDistributedAt: z.date().nullable(),
	resultUrl: localizedStringSchema.nullable(),
	approvedAt: z.date().nullable(),
	startAt: z.date(),
	endAt: z.date(),
	resultAt: z.date(),
	createdAt: z.date(),
	updatedAt: z.date()
});
const contestV2DetailSchema = contestV2Schema.extend({ followAt: z.date().nullable() });
const contestArtworkSchema = z.object({
	id: z.uuidv7(),
	contestId: z.string(),
	userId: z.string(),
	artworkId: z.string(),
	rank: z.number().int().nullable(),
	prizeAmount: z.number().int(),
	source: submitArtworkSourceSchema,
	submittedAt: z.date(),
	createdAt: z.date(),
	updatedAt: z.date()
});
const contestBannerSchema = z.object({
	id: z.uuidv7(),
	contestId: z.string(),
	rank: z.number().int(),
	startAt: z.date(),
	endAt: z.date(),
	enabled: z.boolean(),
	createdAt: z.date(),
	updatedAt: z.date(),
	contest: contestV2Schema
});
const contestV2CreateCheckSchema = z.object({
	eligible: z.boolean(),
	reasons: z.array(z.number().int())
});
const contestListSortSchema = z.enum(["newest", "highestPrize"]);
const listInputSchema = offsetPaginationInputSchema.extend({
	type: contestTypeSchema.optional(),
	sort: contestListSortSchema.default("newest")
});
const createInputSchema = z.object({
	title: localizedStringSchema,
	mediaId: z.string().min(1),
	proposedTackName: proposedTackNameSchema,
	startAt: z.coerce.date(),
	endAt: z.coerce.date(),
	resultAt: z.coerce.date(),
	voteType: z.literal("user_vote"),
	description: localizedStringSchema,
	rules: z.array(contestRuleSchema).default([]),
	prizeAmount: z.number().int().min(5e4).multipleOf(100),
	prizeDistribution: z.array(prizeDistributionItemSchema)
});
const slugOnlyInputSchema = z.object({ slug: slugParamSchema });
const submitArtworkInputSchema = z.object({
	slug: slugParamSchema,
	artworkId: z.coerce.bigint()
});
const listArtworkInputSchema = offsetPaginationInputSchema.extend({
	slug: slugParamSchema,
	sort: listByContestSortSchema.default("newest")
});
const listArtworkByUserInputSchema = z.object({
	slug: slugParamSchema,
	userId: z.coerce.bigint()
});
const listFollowByUserInputSchema = offsetPaginationInputSchema.extend({ userId: z.coerce.bigint() });
const listCreatedByUserInputSchema = offsetPaginationInputSchema.extend({ userId: z.coerce.bigint() });
const followToggleOutputSchema = z.object({ success: z.literal(true) });
const authErr = { UNAUTHORIZED: {
	status: 401,
	message: "User not authenticated"
} };
const notFoundErr = { NOT_FOUND: {
	status: 404,
	message: "Contest not found"
} };
const createErrors = {
	...authErr,
	NOT_ELIGIBLE: {
		status: 403,
		message: "User not eligible"
	},
	INSUFFICIENT_CREDITS: {
		status: 403,
		message: "Insufficient credits"
	},
	INVALID_INPUT: {
		status: 400,
		message: "Invalid input"
	},
	INVALID_REQUIREMENT: {
		status: 400,
		message: "Invalid requirement"
	}
};
const submitErrors = {
	...authErr,
	...notFoundErr,
	CONTEST_NOT_ACTIVE: {
		status: 403,
		message: "Contest is not active"
	},
	SUBMISSION_DEADLINE_PASSED: {
		status: 403,
		message: "Deadline passed"
	},
	ARTWORK_TOO_OLD: {
		status: 400,
		message: "Artwork created before contest"
	},
	ARTWORK_SENSITIVE: {
		status: 403,
		message: "Artwork is sensitive"
	},
	ARTWORK_ALREADY_IN_CONTEST: {
		status: 403,
		message: "Artwork already in use"
	},
	REQUIREMENTS_NOT_MET: {
		status: 400,
		message: "Requirements not met"
	}
};
const contestV2Contract = oc.prefix("/contest").tag("Contest V2").router({
	list: oc.route({
		summary: "List public contests",
		method: "GET",
		path: "/list"
	}).input(listInputSchema).output(createOffsetListResponseSchema(contestV2DetailSchema)),
	listBanner: oc.route({
		summary: "List active contest banners",
		method: "GET",
		path: "/banner"
	}).output(z.object({ data: z.array(contestBannerSchema) })),
	createCheck: oc.route({
		summary: "Check whether current user is eligible to create a contest",
		description: "Lightweight pre-flight for the contest create entry. Always 200. reasons collects every failed check, so the FE can render all missing requirements at once. Codes come from src/constants/errors.ts.",
		method: "GET",
		path: "/create/check"
	}).output(contestV2CreateCheckSchema),
	create: oc.route({
		summary: "Create a community contest",
		method: "POST",
		path: "/create"
	}).input(createInputSchema).output(contestV2Schema).errors(createErrors),
	listFollowByUser: oc.route({
		summary: "List contests followed by a user",
		description: "Ordered by the listed user follow time desc. followAt belongs to the current viewer (null when anonymous or not following). When the viewer is the listed user or an admin, pending contests are included; other viewers see published contests only. Cancelled and draft contests are always hidden.",
		method: "GET",
		path: "/follow/{userId}"
	}).input(listFollowByUserInputSchema).output(createOffsetListResponseSchema(contestV2DetailSchema)),
	listCreatedByUser: oc.route({
		summary: "List contests created by a user",
		description: "Ordered by creation time desc (id desc, since contest ids are snowflake-based). When the viewer is the listed user or an admin, every lifecycleStatus is returned; other viewers see published contests only. Visibility is enforced server-side; clients cannot filter by lifecycleStatus.",
		method: "GET",
		path: "/created/{userId}"
	}).input(listCreatedByUserInputSchema).output(createOffsetListResponseSchema(contestV2DetailSchema)),
	getBySlug: oc.route({
		summary: "Get contest detail by slug",
		method: "GET",
		path: "/{slug}"
	}).input(slugOnlyInputSchema).output(contestV2DetailSchema).errors(notFoundErr),
	submitArtwork: oc.route({
		summary: "Submit an artwork to a contest",
		method: "POST",
		path: "/{slug}/artwork"
	}).input(submitArtworkInputSchema).output(contestArtworkSchema).errors(submitErrors),
	listArtwork: oc.route({
		summary: "List artworks submitted to a contest",
		method: "GET",
		path: "/{slug}/artwork"
	}).input(listArtworkInputSchema).output(createOffsetListResponseSchema(artworkListItemSchema)).errors(notFoundErr),
	listWinners: oc.route({
		summary: "List awarded winners of a contest in rank order",
		description: "Returns [] while the contest is still pre-selection (rewardStatus null or pending_selection). Once winners are selected, returns artworks ordered by rank ascending; card.contest.entry carries rank / prizeAmount / source / submittedAt.",
		method: "GET",
		path: "/{slug}/winners"
	}).input(slugOnlyInputSchema).output(z.array(artworkListItemSchema)).errors(notFoundErr),
	listArtworkByUser: oc.route({
		summary: "List a user's submissions to a contest",
		method: "GET",
		path: "/{slug}/artwork/{userId}"
	}).input(listArtworkByUserInputSchema).output(z.array(contestArtworkSchema)).errors({
		...authErr,
		...notFoundErr
	}),
	follow: oc.route({
		summary: "Follow a contest",
		method: "POST",
		path: "/{slug}/follow"
	}).input(slugOnlyInputSchema).output(followToggleOutputSchema).errors({
		...authErr,
		...notFoundErr
	}),
	unfollow: oc.route({
		summary: "Unfollow a contest",
		method: "POST",
		path: "/{slug}/unfollow"
	}).input(slugOnlyInputSchema).output(followToggleOutputSchema).errors({
		...authErr,
		...notFoundErr
	})
});
//#endregion
export { contestArtworkSchema, contestV2Contract, contestV2CreateCheckSchema, contestV2DetailSchema, contestV2Schema, proposedTackNameSchema };
